import React, { useRef, useEffect } from "react";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";

// Register the required Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

/**
 * Reusable Doughnut Chart component
 * @param {Object} props
 * @param {Array} props.datasets - Chart datasets
 * @param {Object} props.tooltipConfig - Custom tooltip configuration
 * @param {string} props.tooltipConfig.growthIconSrc - Growth icon source
 * @param {string} props.tooltipConfig.growthValue - Growth value to display (e.g. "+12.5%")
 * @param {string} props.tooltipConfig.growthColor - Color for growth value text
 * @param {number} props.width - Chart width in pixels
 * @param {number} props.height - Chart height in pixels
 * @param {Object} props.additionalOptions - Additional Chart.js options to merge
 */
const DoughnutChart = ({
  datasets,
  tooltipConfig = {
    growthIconSrc: "/icons/assesssment-chart-increase.svg",
    growthValue: "+12.5%",
    growthColor: "#5CE0A0",
  },
  width = 300,
  height = 300,
  additionalOptions = {},
}) => {
  const chartRef = useRef(null);
  const tooltipElRef = useRef(null);
  const chartContainerRef = useRef(null);

  useEffect(() => {
    const tooltipEl = document.createElement("div");
    tooltipEl.classList.add("custom-tooltip");
    tooltipEl.style.opacity = 0;
    tooltipEl.style.position = "absolute";
    tooltipEl.style.zIndex = 10;
    tooltipEl.style.pointerEvents = "none";
    tooltipEl.style.transition = "opacity 0.2s ease";

    const chartContainer = chartContainerRef.current; // Store ref value in a variable

    if (chartContainer) {
      chartContainer.appendChild(tooltipEl);
    }

    tooltipElRef.current = tooltipEl;

    // Cleanup function
    return () => {
      if (
        tooltipElRef.current &&
        chartContainer?.contains(tooltipElRef.current)
      ) {
        chartContainer.removeChild(tooltipElRef.current);
      }
    };
  }, []);

  const data = {
    datasets: datasets,
  };

  const defaultOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: false,
        external: function (context) {
          const tooltipEl = tooltipElRef.current;
          if (!tooltipEl) return;

          const tooltipModel = context.tooltip;
          if (tooltipModel.opacity === 0) {
            tooltipEl.style.opacity = 0;
            return;
          }

          if (tooltipModel.body) {
            const dataPoint = tooltipModel.dataPoints[0];
            if (dataPoint.dataIndex !== 0) {
              tooltipEl.style.opacity = 0;
              return;
            }

            const label = dataPoint.dataset.label || "";
            const value = dataPoint.raw;
            const backgroundColor = dataPoint.dataset.backgroundColor[0];

            // Get growth value from the dataset (added in step 1)
            const growthValueNum = dataPoint.dataset.growthPercentage || 0;
            const growthValue =
              (growthValueNum >= 0 ? "+" : "") +
              growthValueNum.toFixed(1) +
              "%";
            const growthColor = growthValueNum >= 0 ? "#5CE0A0" : "#FF4D4F";
            const growthIconSrc =
              growthValueNum >= 0
                ? "/icons/assesssment-chart-increase.svg"
                : "/icons/assessment-chart-decrease.svg";

            tooltipEl.innerHTML = `
      <div class="tooltip-content">
        <div class="tooltip-dot" style="background:${backgroundColor}; "></div>
        <div class="tooltip-details">
          <div class="tooltip-label text-body-b3">${label}</div>
          <div class="tooltip-value text-caption-c2" >
            <span>%${value}</span> 
            <div class="chart-growth-dough" >
              <img src="${growthIconSrc}" alt="Growth indicator" />
              <span style="color:${growthColor}">${growthValue}</span>
            </div>
          </div>
        </div>
      </div>
    `;
          }

          // Position tooltip relative to chart container
          const position = context.chart.canvas.getBoundingClientRect();
          const containerPos =
            chartContainerRef.current.getBoundingClientRect();

          // Calculate position relative to container
          const relativeX =
            position.left - containerPos.left + tooltipModel.caretX;
          const relativeY =
            position.top - containerPos.top + tooltipModel.caretY;

          tooltipEl.style.left = relativeX + "px";
          tooltipEl.style.top = relativeY + "px";
          tooltipEl.style.transform = "translate(-50%, -100%)";
          tooltipEl.style.opacity = 1;
        },
      },
    },
  };

  // Merge default options with any additional options provided
  const options = { ...defaultOptions, ...additionalOptions };

  return (
    <div
      ref={chartContainerRef}
      style={{
        height: `${height}px`,
        width: `${width}px`,
        position: "relative",
      }}
    >
      <Doughnut ref={chartRef} data={data} options={options} />
    </div>
  );
};

export default DoughnutChart;
